/**
 * Blocks third-party writes to merged bundle cart lines.
 *
 * Divvy and bundle lines are added as quantity-1 lines that share a
 * `_bundle_id`, and a Cart Transform function merges them into one priced
 * line. The merged line carries presentation properties that the source lines
 * never had: `_merged_bundle` and `_bundle_parent_variant_id`.
 *
 * Visually.io tags the shopper's A/B group by calling `/cart/change.js` from
 * its `setGroupOnCartItem` function, targeting the merged line's key. Because
 * `change.js` replaces the whole properties object rather than merging into
 * it, that call writes the merged line's properties back down onto the source
 * lines, with `_cgroup` added. The pricing properties the Cart Transform needs
 * (`_bundle_final_price`, `_bundle_items`, `_bundle_price`) are not in that
 * object, so they are lost and the bundle discount stops applying.
 *
 * Observed on the Original Starter Kit bundle: the cart total became $126.00
 * (the undiscounted sum of the three lines) instead of $125.00, and the call
 * fired twice per add.
 *
 * The theme never needs to write `_merged_bundle` itself, since only the Cart
 * Transform sets it. So any such request is third-party by definition.
 *
 * This guard wraps `fetch` and `XMLHttpRequest`. It aborts a `change.js`
 * request when the payload carries `properties._merged_bundle === 'true'` and
 * the request does not come from the theme.
 *
 * Theme code marks its own requests with the `FIRST_PARTY_FLAG` property. The
 * guard strips that flag before the request goes to Shopify, so it never
 * reaches the cart. Use `window.theme.cartBundleGuard.markFirstParty(body)` to
 * mark a payload.
 *
 * Load this file in the `<head>` before any third-party script.
 */
(function () {
  'use strict';

  /** @type {string} Property name that marks a theme-issued request. */
  const FIRST_PARTY_FLAG = '__themeFirstParty';

  /** @type {RegExp} Matches the cart change endpoint on any locale prefix. */
  const CHANGE_URL_PATTERN = /\/cart\/change(\.js)?(\?|$)/;

  /**
   * Reads the URL from a `fetch` argument.
   *
   * @param {RequestInfo|URL} input - The first `fetch` argument.
   * @returns {string} The URL, or an empty string when it cannot be read.
   */
  const readUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  };

  /**
   * Tests whether a URL targets the cart change endpoint.
   *
   * @param {string} url - The request URL.
   * @returns {boolean} True when the URL is a cart change request.
   */
  const isChangeRequest = (url) => CHANGE_URL_PATTERN.test(String(url).split('#')[0]);

  /**
   * Parses a request body into a plain object.
   *
   * Handles the JSON and form-encoded bodies that the Cart API accepts. Returns
   * null for a body this guard cannot read, such as FormData or a stream.
   *
   * @param {*} body - The request body.
   * @returns {Object|null} The parsed payload, or null.
   */
  const parseBody = (body) => {
    if (!body) return null;
    if (typeof body === 'object' && !(body instanceof URLSearchParams) && typeof body !== 'string') {
      return body;
    }

    const raw = String(body);

    try {
      return JSON.parse(raw);
    } catch (error) {
      // Not JSON, so fall through to the form-encoded reader below.
    }

    try {
      const params = new URLSearchParams(raw);
      const parsed = {};
      const properties = {};
      let hasProperties = false;

      for (const [key, value] of params) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) {
          properties[match[1]] = value;
          hasProperties = true;
          continue;
        }
        parsed[key] = value;
      }

      if (hasProperties) parsed.properties = properties;
      return parsed;
    } catch (error) {
      return null;
    }
  };

  /**
   * Tests whether a payload writes to a merged bundle line.
   *
   * @param {Object|null} payload - The parsed request payload.
   * @returns {boolean} True when the payload carries the merged bundle flag.
   */
  const targetsMergedBundle = (payload) => {
    const properties = payload && payload.properties;
    if (!properties || typeof properties !== 'object') return false;
    return String(properties._merged_bundle) === 'true';
  };

  /**
   * Tests whether the theme issued this request.
   *
   * @param {Object|null} payload - The parsed request payload.
   * @returns {boolean} True when the payload carries the first-party flag.
   */
  const isFirstParty = (payload) => Boolean(payload && payload[FIRST_PARTY_FLAG] === true);

  /**
   * Reports a blocked request, and lets other code react to it.
   *
   * @param {string} url - The blocked request URL.
   * @param {Object} payload - The blocked request payload.
   * @returns {void}
   */
  const reportBlocked = (url, payload) => {
    console.warn(
      '[cart-bundle-guard] Blocked a third-party /cart/change.js request against a merged bundle line.',
      { url: url, payload: payload }
    );

    document.dispatchEvent(
      new CustomEvent('cart:bundle-guard:blocked', { detail: { url: url, payload: payload } })
    );
  };

  /**
   * Removes the first-party flag from a payload before it leaves the browser.
   *
   * @param {Object} payload - The parsed request payload.
   * @returns {Object} A copy of the payload with the flag removed.
   */
  const stripFlag = (payload) => {
    const clean = Object.assign({}, payload);
    delete clean[FIRST_PARTY_FLAG];
    return clean;
  };

  const nativeFetch = window.fetch;

  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      try {
        const url = readUrl(input);

        if (isChangeRequest(url)) {
          const hasInitBody = init && init.body !== undefined && init.body !== null;
          const body = hasInitBody ? init.body : input && input.body;
          const payload = parseBody(body);

          if (targetsMergedBundle(payload)) {
            if (!isFirstParty(payload)) {
              reportBlocked(url, payload);
              // Resolve rather than reject: a rejection surfaces as an uncaught
              // error in the caller, and the caller here is third-party code.
              return Promise.resolve(
                new Response(JSON.stringify({ status: 422, message: 'Blocked', description: 'Blocked by cart bundle guard' }), {
                  status: 422,
                  headers: { 'Content-Type': 'application/json' }
                })
              );
            }

            // A first-party request: drop the marker so Shopify never sees it.
            if (hasInitBody && typeof init.body === 'string') {
              const nextInit = Object.assign({}, init, { body: JSON.stringify(stripFlag(payload)) });
              return nativeFetch.call(this, input, nextInit);
            }
          }
        }
      } catch (error) {
        // Never let the guard break a cart request.
        console.error('[cart-bundle-guard]', error);
      }

      return nativeFetch.call(this, input, init);
    };
  }

  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cartGuardUrl = url;
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    try {
      if (isChangeRequest(this.__cartGuardUrl || '')) {
        const payload = parseBody(body);

        if (targetsMergedBundle(payload) && !isFirstParty(payload)) {
          reportBlocked(this.__cartGuardUrl, payload);
          this.abort();
          return undefined;
        }
      }
    } catch (error) {
      console.error('[cart-bundle-guard]', error);
    }

    return nativeSend.apply(this, arguments);
  };

  window.theme = window.theme || {};
  window.theme.cartBundleGuard = {
    FIRST_PARTY_FLAG: FIRST_PARTY_FLAG,

    /**
     * Marks a payload as theme-issued, so the guard lets it through.
     *
     * @param {Object} payload - The request payload.
     * @returns {Object} A copy of the payload with the first-party flag set.
     */
    markFirstParty: (payload) => Object.assign({}, payload, { [FIRST_PARTY_FLAG]: true })
  };
})();
